/** Column visibility menu component for showing/hiding table columns. */
import React, { useState, useEffect } from "react";
import {
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  Divider,
  Button,
  Box,
  Tooltip,
} from "@mui/material";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { Table } from "@tanstack/react-table";

export interface ColumnDefinition {
  id: string;
  header: string;
  required?: boolean; // If true, column cannot be hidden
}

interface ColumnVisibilityMenuProps {
  table: Table<any>;
  columns: ColumnDefinition[];
  storageKey?: string; // localStorage key for persistence
}

/**
 * Column visibility menu with checkbox list.
 * Shows/hides table columns with persistence to localStorage.
 */
export default function ColumnVisibilityMenu({
  table,
  columns,
  storageKey,
}: ColumnVisibilityMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  /** Load column visibility from localStorage on mount. */
  useEffect(() => {
    if (!storageKey) return;

    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const visibility = JSON.parse(saved);
        // Apply saved visibility to each column
        columns.forEach((col) => {
          const column = table.getColumn(col.id);
          if (column && visibility[col.id] !== undefined) {
            column.toggleVisibility(visibility[col.id]);
          }
        });
      }
    } catch (error) {
      console.error("Failed to load column visibility:", error);
    }
  }, [table, columns, storageKey]);

  /** Save column visibility to localStorage. */
  const saveVisibility = () => {
    if (!storageKey) return;

    try {
      const visibility: Record<string, boolean> = {};
      columns.forEach((col) => {
        const column = table.getColumn(col.id);
        if (column) {
          visibility[col.id] = column.getIsVisible();
        }
      });
      localStorage.setItem(storageKey, JSON.stringify(visibility));
    } catch (error) {
      console.error("Failed to save column visibility:", error);
    }
  };

  /** Handle menu open. */
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  /** Handle menu close. */
  const handleClose = () => {
    setAnchorEl(null);
  };

  /** Handle column visibility toggle. */
  const handleToggle = (columnId: string) => {
    const column = table.getColumn(columnId);
    if (column) {
      column.toggleVisibility();
      saveVisibility();
    }
  };

  /** Show all columns. */
  const handleShowAll = () => {
    columns.forEach((col) => {
      if (!col.required) {
        const column = table.getColumn(col.id);
        if (column && !column.getIsVisible()) {
          column.toggleVisibility(true);
        }
      }
    });
    saveVisibility();
  };

  /** Hide all optional columns. */
  const handleHideAll = () => {
    columns.forEach((col) => {
      if (!col.required) {
        const column = table.getColumn(col.id);
        if (column && column.getIsVisible()) {
          column.toggleVisibility(false);
        }
      }
    });
    saveVisibility();
  };

  return (
    <>
      <Tooltip title="Column visibility">
        <IconButton
          onClick={handleClick}
          size="small"
          aria-label="Column visibility"
        >
          <ViewColumnIcon />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            maxHeight: 400,
            width: 250,
          },
        }}
      >
        {columns.map((col) => {
          const column = table.getColumn(col.id);
          if (!column) return null;

          const isVisible = column.getIsVisible();

          return (
            <MenuItem
              key={col.id}
              onClick={() => !col.required && handleToggle(col.id)}
              disabled={col.required}
              dense
            >
              <Checkbox
                checked={isVisible}
                size="small"
                disabled={col.required}
              />
              <ListItemText primary={col.header} />
            </MenuItem>
          );
        })}
        <Divider />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, p: 1 }}>
          <Button
            onClick={handleShowAll}
            size="small"
            variant="outlined"
            fullWidth
          >
            Show All
          </Button>
          <Button
            onClick={handleHideAll}
            size="small"
            variant="outlined"
            fullWidth
          >
            Hide All
          </Button>
        </Box>
      </Menu>
    </>
  );
}
